import React from 'react'
import { toast } from 'react-toastify'
import {
    Box,
    Button,
    Grid,
    MenuItem,
    Select,
    Switch,
    TextField
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useMutation } from '@tanstack/react-query'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const formikInitialValues = {
    name: '',
    displayName: '',
    destinationType: 'SINGLE', // Changed to a valid option
    visibility: false,
}

const NewPageSchema = Yup.object().shape({
    name: Yup.string().required('Page name is required'),
    displayName: Yup.string().required('displayName is required'),
    visibility: Yup.boolean(),
    destinationType: Yup.string() // Changed from DestinationType
        .required('Input is required'),
})

const CenteredBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: '80%'
})
const CenteredGrid = styled(Grid)({
    display: 'flex',
    alignItems: 'center'
})

const StyledTextField = styled(TextField)({
    '& input': {
        padding: '6px'
    },
    width: '100%'
})

export const NewPageForm = ({
    handleClose
}: {
    handleClose: () => void
}) => {
    const faberToken = OrbitSession.get('faberToken')

    /**
     * Event handlers
     */
    const handleDestinationSelect = (
        e,
        setFieldValue: (
            field: string,
            value,
            shouldValidate?: boolean | undefined
        ) => void
    ) => {
        setFieldValue(e.target.name, e.target.value)
    }

    const { mutate: createPage } = useMutation(CatalogAPI.createCatalogPage, {
        onSuccess: () => {
            toast.success('Page created successfully!')
            handleClose()
        },
        onError: () => {
            toast.error('Failed to create Page!')
        }
    })

    const handleFormSubmit = (values: NewPageFormikProps) => {
        const allValues = {
            name: values.name,
            displayName: values.displayName,
            destinationType: values.destinationType,
            visibility: values.visibility,
            faberToken: faberToken
        }
        const allValuesArray = [allValues];
        createPage(allValuesArray)
    }


    return (
        <Formik
            initialValues={formikInitialValues}
            onSubmit={handleFormSubmit}
            validationSchema={NewPageSchema}
        >
            {({
                handleChange,
                errors,
                touched,
                handleBlur,
                setFieldValue,
                submitForm
            }) => (
                <Form>
                    <Grid container rowSpacing={2}>
                        <Grid item xs={3}>
                            <NormalText isBold>Page Name</NormalText>
                        </Grid>
                        <Grid item xs={9}>
                            <Box sx={{ width: '80%' }}>
                                <StyledTextField
                                    name='name'
                                    onChange={(e) => {
                                        handleBlur(e)
                                        handleChange(e)
                                    }}
                                    inputProps={{
                                        'data-testid': 'page-name'
                                    }}
                                    fullWidth
                                    variant='outlined'
                                    error={!!(errors.name && touched.name)}
                                    helperText={errors.name && touched.name ? errors.name : null}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={3}>
                            <NormalText isBold>Display Name</NormalText>
                        </Grid>
                        <CenteredGrid item xs={9}>
                            <CenteredBox>
                                <StyledTextField
                                    name='displayName'
                                    type='text'
                                    onChange={(e) => {
                                        handleBlur(e)
                                        handleChange(e)
                                    }}
                                    error={
                                        !!(
                                            errors.displayName &&
                                            touched.displayName
                                        )
                                    }
                                    helperText={
                                        errors.displayName &&
                                            touched.displayName
                                            ? errors.displayName
                                            : null
                                    }
                                />
                            </CenteredBox>
                        </CenteredGrid>
                        <Grid item xs={3}>
                            <NormalText isBold>Destination Type</NormalText>
                        </Grid>
                        <Grid item xs={6}>
                            <Select
                                variant='standard'
                                sx={{ width: '80%' }}
                                onChange={(e) => handleDestinationSelect(e, setFieldValue)}
                                name='destinationType'
                                defaultValue='SINGLE' // Matches initial value
                            >
                                <MenuItem value='MULTI'>MULTI</MenuItem>
                                <MenuItem value='SINGLE'>SINGLE</MenuItem>
                                <MenuItem value='PROMO'>PROMO</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={3}>
                            <NormalText isBold>Visibility
                                <Switch
                                    onChange={(e) => setFieldValue('visibility', e.target.checked)} // Correctly set boolean value
                                    name='visibility'
                                />
                            </NormalText>
                        </Grid>
                    </Grid>
                    <Grid>
                        <Box mt={3}>
                            <Button onClick={submitForm}>Create Page</Button>
                        </Box>

                    </Grid>
                </Form>
            )}
        </Formik>
    )
}
interface NewPageFormikProps {
    name: string
    displayName: string,
    destinationType: string,
    visibility: boolean
}
